import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { LANGUAGES } from 'projects/core/src/lib/constants';
import { SelectItem } from 'primeng/api';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { EXERCISETYPES, ExerciseModel, ExerciseType } from 'projects/core/src/lib/models/exercise.model';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { HelperService } from 'projects/admin/src/app/services/helper.service';

@Component({
  selector: 'app-exercise-new',
  templateUrl: './exercise-new.component.html',
  styleUrls: ['./exercise-new.component.scss']
})
export class ExerciseNewComponent extends BaseForm implements OnInit, OnDestroy {

  public exercise: ExerciseModel = null;
  public messages: Array<any> = [];
  public languages: Array<SelectItem> = [];
  public types: Array<SelectItem> = [];
  public form: UntypedFormGroup = this.fb.group({
    for_single: new UntypedFormControl(''),
    for_group: new UntypedFormControl(''),
    name: new UntypedFormControl('', [Validators.required]),
    title: new UntypedFormControl('', [Validators.required]),
    language: new UntypedFormControl('', [Validators.required]),
    description: new UntypedFormControl(''),
    group_settings: this.fb.group({
      type: new UntypedFormControl(''),
      min_observers_per_candidate: new UntypedFormControl(''),
      min_candidates_per_team: new UntypedFormControl(''),
      overlapping: new UntypedFormControl(''),
    })
  });
  public subscriptions: Array<Subscription> = [];

  constructor(
    private api: ApiService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private transloco: TranslocoService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit(): void {
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.types = FormHelper.getSelectList(EXERCISETYPES, null, this.transloco);
    this.subscriptions.push(this.form.get('for_group').valueChanges.subscribe(() => {
      this.setFormSettings();
    }));
    this.subscriptions.push(this.form.get('group_settings.type').valueChanges.subscribe(() => {
      this.setTeamSettings();
    }));
    this.ui.setFormFocus();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public onCancel(): void {
    this.router.navigate(['/exercises']);
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      this.api.saveModel('exercises', null, this.getPayload()).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.router.navigate(['/exercises', result.data.id]);
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public hasGroupSettings(): boolean {
    return this.form.get('for_group').value === true;
  }

  public isTeam(): boolean {
    return this.form.get('group_settings.type').value === ExerciseType.Team;
  }

  private getPayload(): any {
    return {
      for_single: this.form.get('for_single').value,
      for_group: this.form.get('for_group').value,
      name: this.form.get('name').value,
      title: this.form.get('title').value,
      language: this.form.get('language').value,
      description: this.form.get('description').value,
      group_settings: (this.form.get('for_group').value === true) ? this.form.get('group_settings').value : null,
    };
  }

  private setFormSettings(): void {
    if (this.form.get('for_group').value === true) {
      this.form.get('group_settings.type').setValidators([Validators.required]);
      this.form.get('group_settings.min_observers_per_candidate').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('group_settings.type').clearValidators();
      this.form.get('group_settings.min_observers_per_candidate').clearValidators();
    }
    this.form.get('group_settings.type').updateValueAndValidity();
    this.form.get('group_settings.min_observers_per_candidate').updateValueAndValidity();
  }

  private setTeamSettings(): void {
    if (this.form.get('group_settings.type').value === ExerciseType.Team) {
      this.form.get('group_settings.min_candidates_per_team').setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('group_settings.min_candidates_per_team').clearValidators();
    }
    this.form.get('group_settings.min_candidates_per_team').updateValueAndValidity();
  }

}
